import { Sop, SopTypes } from '@energybox/react-ui-library/dist/types';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSopByOrganizationalUnitId } from '../actions/sops';
import { ApplicationState } from '../reducers';
import { sanitizeApiLoadingState } from '../util';
import { Efficiency } from '@energybox/react-ui-library/dist/types/Sop';

export interface EUITargetResult {
  euiTarget: number | undefined;
  isLoading: boolean;
}

/**
 * Hook to fetch and retrieve EUI efficiency SOP for an organization.
 * Checks if SOPs are already in state for the orgId, and fetches if not.
 *
 * @param orgId - The organization unit ID
 * @returns Object containing the efficiency SOP and loading state
 */
export const useEUITarget = (
  orgId: string | number | undefined | null
): EUITargetResult => {
  const dispatch = useDispatch();

  // Get all SOPs for this orgId from state
  const sopsByOrgId = useSelector<ApplicationState, Sop[] | undefined>(
    ({ sops }) => {
      if (!orgId) return undefined;
      const sopsArray = sops.sopsById[String(orgId)];
      // The reducer stores an array in sopsById[orgId]
      return Array.isArray(sopsArray) ? sopsArray : undefined;
    }
  );

  const isLoading = useSelector<ApplicationState, boolean>(
    ({ sops }) => sops.isLoading
  );

  // Check if we need to fetch - if sopsByOrgId doesn't exist, we should fetch
  const hasData = sopsByOrgId !== undefined;

  // Fetch SOPs if orgId is provided and data is not yet loaded
  useEffect(() => {
    if (!orgId || hasData) {
      return;
    }

    dispatch(getSopByOrganizationalUnitId(orgId));
  }, [orgId, hasData, dispatch]);

  // Find the efficiency SOP from the array
  const efficiencySop = useMemo(() => {
    if (!sopsByOrgId || !orgId) {
      return undefined;
    }

    const normalizedOrgId = String(orgId);
    return sopsByOrgId.find((item) => {
      return (
        item.components[0]?.type === SopTypes.EUI_EFFICIENCY &&
        String(item.organizationUnitId) === normalizedOrgId
      );
    });
  }, [sopsByOrgId, orgId]);

  const euiTarget = isLoading
    ? undefined
    : (efficiencySop?.components[0] as Efficiency)?.euiTarget || undefined;

  return useMemo(
    () => ({
      euiTarget,
      isLoading: sanitizeApiLoadingState(isLoading),
    }),
    [euiTarget, isLoading]
  );
};
